import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Award, Mail, AlertCircle, RefreshCw, LogOut } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { apiPost } from '../../lib/api'

export default function VerifyEmailPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleResend = async () => {
    setError('')
    setIsResending(true)
    try {
      await apiPost('/auth/resend-verification')
      setResendSuccess(true)
      setTimeout(() => setResendSuccess(false), 5000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend verification email')
    } finally {
      setIsResending(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Logo */}
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
          <p className="text-gray-500 mb-6">
            We've sent a verification link to{' '}
            <strong className="text-gray-700">{user?.email}</strong>. 
            Please check your inbox and click the link to verify your account.
          </p>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-left">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Success message */}
          {resendSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
              <p className="text-sm text-green-600">Verification email sent successfully!</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleRefresh}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              I've Verified - Refresh
            </button>

            <button
              onClick={handleResend}
              disabled={isResending}
              className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 disabled:opacity-50"
            >
              {isResending ? 'Sending...' : "Didn't receive it? Resend"}
            </button>

            <button
              onClick={handleLogout}
              className="w-full py-3 px-4 text-gray-500 hover:text-gray-700 font-medium flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-6">
            Make sure to check your spam folder if you don't see the email.
          </p>
        </div>
      </div>
    </div>
  )
}
